/**
 * Task #3453 — Schemi zod del kernel variabili/funzioni v2.
 *
 * Boundary input per: AST funzioni v2 (superset retro-compatibile del
 * condition tree legacy), metadata kernel delle variabili, pubblicazione/
 * archiviazione e simulazione. I tipi dei body validati sono derivati con
 * `z.infer` (docs/zod-boundary.md). Zero `any`.
 */
import { z } from 'zod';
import {
  COMMISSIONING_CONDITION_OPERATORS,
  COMMISSIONING_EVENT_SOURCE_TYPES,
} from '../types/commissioning';
import {
  COMMISSIONING_FUNCTION_EFFECT_TARGETS,
  COMMISSIONING_KERNEL_DATA_TYPES,
  COMMISSIONING_MISSING_POLICIES,
  COMMISSIONING_RESOLUTION_STATES,
  COMMISSIONING_SET_OPERATORS,
  COMMISSIONING_VARIABLE_CARDINALITIES,
  COMMISSIONING_VARIABLE_PHASES,
  COMMISSIONING_VARIABLE_SOURCE_KINDS,
  COMMISSIONING_VARIABLE_SUBJECTS,
  COMMISSIONING_WIRING_STATUSES,
  type CommissioningFunctionEffect,
  type CommissioningKernelNode,
} from '../types/commissioning-kernel';

const scalarValueSchema = z.union([z.string(), z.number(), z.boolean()]);

const variableRefSchema = z
  .string()
  .regex(/^@[a-z][a-z0-9_]*$/, 'Variabile nel formato @snake_case') as z.ZodType<`@${string}`>;

// ---------------------------------------------------------------------------
// AST v2 (ricorsivo, superset del condition tree legacy)
// ---------------------------------------------------------------------------

const kernelCompareLeafSchema = z
  .object({
    kind: z.literal('condition'),
    variable: variableRefSchema,
    operator: z.enum(COMMISSIONING_CONDITION_OPERATORS),
    value: scalarValueSchema.optional(),
    values: z.array(scalarValueSchema).max(500).optional(),
    listRefId: z.string().uuid().optional(),
    rightVariable: variableRefSchema.optional(),
  })
  .superRefine((v, ctx) => {
    if (v.operator === 'between') {
      if (!v.values || v.values.length !== 2) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['values'],
          message: 'L\'operatore between richiede esattamente due valori [min, max]',
        });
      }
    }
    if (v.operator === 'in_last_days' || v.operator === 'in_next_days') {
      if (typeof v.value !== 'number' || !Number.isInteger(v.value) || v.value < 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['value'],
          message: 'Gli operatori data-relativa richiedono `value` = numero intero di giorni ≥ 0',
        });
      }
      if (v.rightVariable !== undefined) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['rightVariable'],
          message: 'Gli operatori data-relativa non supportano il confronto variabile-variabile',
        });
      }
    }
  });

const kernelStateLeafSchema = z.object({
  kind: z.literal('state'),
  variable: variableRefSchema,
  test: z.enum(COMMISSIONING_RESOLUTION_STATES),
});

const kernelSetLeafSchema = z
  .object({
    kind: z.literal('set_op'),
    variable: variableRefSchema,
    operator: z.enum(COMMISSIONING_SET_OPERATORS),
    values: z.array(z.string().min(1).max(160)).max(500).optional(),
    listRefId: z.string().uuid().optional(),
    size: z.number().int().min(0).optional(),
  })
  .superRefine((v, ctx) => {
    const isSize = v.operator.startsWith('set_size');
    if (isSize && v.size === undefined) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['size'],
        message: 'Gli operatori di cardinalità richiedono `size`',
      });
    }
    if (!isSize && !v.listRefId && (v.values === undefined || v.values.length === 0)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['values'],
        message: 'Gli operatori di contenimento richiedono `values` o `listRefId`',
      });
    }
  });

export const commissioningKernelNodeSchema: z.ZodType<CommissioningKernelNode> = z.lazy(() =>
  z.union([
    kernelCompareLeafSchema,
    kernelStateLeafSchema,
    kernelSetLeafSchema,
    z.object({
      kind: z.enum(['and', 'or']),
      children: z.array(commissioningKernelNodeSchema).min(1).max(50),
    }),
    z.object({
      kind: z.literal('not'),
      child: commissioningKernelNodeSchema,
    }),
    z.object({
      kind: z.literal('func_ref'),
      functionId: z.string().uuid(),
      version: z.number().int().min(1).optional(),
    }),
  ])
);

// ---------------------------------------------------------------------------
// Metadata kernel delle variabili (campi addizionali create/update)
// ---------------------------------------------------------------------------

const perimeterSchema = z.object({
  driverIds: z.array(z.string().uuid()).max(100).optional(),
  categoryIds: z.array(z.string().uuid()).max(200).optional(),
  productTypeIds: z.array(z.string().uuid()).max(200).optional(),
  productIds: z.array(z.string().uuid()).max(500).optional(),
});

/** Schema Zod per CommissioningVariableSelectSource. */
const selectSourceDomainEnumSchema = z.object({
  kind: z.literal('domain_enum'),
  values: z.array(z.object({ value: z.string().min(1), label: z.string().min(1) })).min(1).max(500),
});

const selectSourceQuerySchema = z.object({
  kind: z.literal('query'),
  endpoint: z.string().min(1).max(80),
});

const selectSourceReferenceListSchema = z.object({
  kind: z.literal('reference_list'),
  listId: z.string().uuid(),
});

export const commissioningVariableSelectSourceSchema = z.discriminatedUnion('kind', [
  selectSourceDomainEnumSchema,
  selectSourceQuerySchema,
  selectSourceReferenceListSchema,
]);

/** Campi kernel opzionali accettati da create/update variabile. */
export const commissioningVariableKernelFieldsSchema = z.object({
  data_type: z.enum(COMMISSIONING_KERNEL_DATA_TYPES).optional(),
  source_kind: z.enum(COMMISSIONING_VARIABLE_SOURCE_KINDS).optional(),
  subject_kind: z.enum(COMMISSIONING_VARIABLE_SUBJECTS).optional(),
  min_phase: z.enum(COMMISSIONING_VARIABLE_PHASES).optional(),
  cardinality: z.enum(COMMISSIONING_VARIABLE_CARDINALITIES).optional(),
  family_coverage: z.array(z.enum(COMMISSIONING_EVENT_SOURCE_TYPES)).min(1).max(3).optional(),
  wiring_status: z.enum(COMMISSIONING_WIRING_STATUSES).optional(),
  missing_policy: z.enum(COMMISSIONING_MISSING_POLICIES).optional(),
  unit: z.string().max(30).nullable().optional(),
  value_precision: z.number().int().min(0).max(8).nullable().optional(),
  depends_on: z.array(z.string().min(1).max(80).regex(/^[a-z][a-z0-9_]*$/)).max(25).optional(),
  perimeter: perimeterSchema.nullable().optional(),
  /** Fonte valori per single_select/multi_select (null = nessuna sorgente dichiarata). */
  select_source: commissioningVariableSelectSourceSchema.nullable().optional(),
});

// ---------------------------------------------------------------------------
// Governance funzioni: pubblicazione, archiviazione, nuova versione variabile
// ---------------------------------------------------------------------------

export const publishCommissioningFunctionSchema = z.object({
  valid_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
});

// ---------------------------------------------------------------------------
// Task #3482 — Effetti economici delle Funzioni (THEN/ELSE branch)
// ---------------------------------------------------------------------------

/** Targets delta (valenza/gettoni): op add|subtract + valore positivo. */
const functionEffectDeltaSchema = z
  .object({
    target: z.enum(['valenza', 'gettone_contrattuale', 'gettone_gara'] as const),
    op: z.enum(['add', 'subtract'] as const),
    value: z.number().finite().nonnegative('Il valore dell\'effetto deve essere ≥ 0'),
  })
  .strict();

/** Target fattore canone: op multiply + factor > 0. */
const functionEffectFactorSchema = z
  .object({
    target: z.literal('canone_factor' as const),
    op: z.literal('multiply' as const),
    factor: z.number().finite().positive('Il fattore moltiplicativo deve essere > 0'),
  })
  .strict();

/**
 * Schema di un singolo effetto economico dichiarato da una Funzione.
 * Discriminato su `target` (non su `op` per compatibilità futura).
 */
export const commissioningFunctionEffectSchema: z.ZodType<CommissioningFunctionEffect> = z.union([
  functionEffectDeltaSchema,
  functionEffectFactorSchema,
]);

/** Rotazione versione variabile (pattern value-package: nuova testa con decorrenza). */
export const rotateCommissioningVariableVersionSchema = z.object({
  valid_from: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  name: z.string().min(1).max(160).optional(),
  description: z.string().max(2000).nullable().optional(),
  resolver_key: z
    .string()
    .min(3)
    .max(160)
    .regex(/^[a-zA-Z_][a-zA-Z0-9_]*(\.[a-zA-Z_][a-zA-Z0-9_]*)+$/)
    .optional(),
}).merge(commissioningVariableKernelFieldsSchema);

// ---------------------------------------------------------------------------
// Task #3522 — Abbinamento istanza×funzione (leve esplicitate)
// ---------------------------------------------------------------------------

/**
 * Schema di UN set di leve di un abbinamento istanza×funzione
 * (usato sia per `match_levers` che per `else_levers`).
 *
 * Riusa i componenti delta/factor. Vincoli per set:
 *   - max 1 voce canone_factor per set (fattori di abbinamenti diversi
 *     si moltiplicano a runtime — ma una sola leva per set).
 *   - nessun target duplicato nello stesso set.
 *
 * Un abbinamento con ENTRAMBI i set vuoti è strutturalmente valido ma INERTE
 * a runtime; il preflight lo segnala come finding bloccante prima della
 * pubblicazione.
 */
export const commissioningInstanceFunctionLeversSchema = z
  .array(commissioningFunctionEffectSchema)
  .max(10, 'Massimo 10 leve per set')
  .refine(
    (arr) => arr.filter((e) => e.target === 'canone_factor').length <= 1,
    { message: 'canone_factor: ammessa al massimo una leva per set' },
  )
  .refine(
    (arr) => {
      const seen = new Set<string>();
      for (const e of arr) {
        if (seen.has(e.target)) return false;
        seen.add(e.target);
      }
      return true;
    },
    { message: 'Target duplicato: ogni target può comparire al massimo una volta per set' },
  );

/** Aggiunge una funzione a un'istanza con i due set di leve esplicitati. */
export const addInstanceFunctionAssignmentSchema = z.object({
  function_id: z.string().uuid('function_id deve essere un UUID valido'),
  sequence: z.number().int().min(0).max(32767).optional(),
  /** Leve applicate quando la condizione è MATCH ("Se matcha"). */
  match_levers: commissioningInstanceFunctionLeversSchema,
  /** Leve applicate quando la condizione è NO_MATCH ("Altrimenti"). */
  else_levers: commissioningInstanceFunctionLeversSchema,
});

/** Aggiorna le leve e/o la sequenza di un abbinamento esistente. */
export const updateInstanceFunctionAssignmentSchema = z.object({
  sequence: z.number().int().min(0).max(32767).optional(),
  match_levers: commissioningInstanceFunctionLeversSchema.optional(),
  else_levers: commissioningInstanceFunctionLeversSchema.optional(),
});

/** Riordina gli abbinamenti di un'istanza (sostituisce le sequence). */
export const reorderInstanceFunctionAssignmentsSchema = z.object({
  assignment_ids: z.array(z.string().uuid()).min(0).max(200),
});

export type AddInstanceFunctionAssignmentInput =
  z.infer<typeof addInstanceFunctionAssignmentSchema>;
export type UpdateInstanceFunctionAssignmentInput =
  z.infer<typeof updateInstanceFunctionAssignmentSchema>;
export type ReorderInstanceFunctionAssignmentsInput =
  z.infer<typeof reorderInstanceFunctionAssignmentsSchema>;

// ---------------------------------------------------------------------------
// Simulazione funzione (workspace FE)
// ---------------------------------------------------------------------------

const simulationValueSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
  z.array(z.string().max(200)).max(200),
]);

export const simulateCommissioningFunctionSchema = z.object({
  family: z.enum(COMMISSIONING_EVENT_SOURCE_TYPES),
  values: z.record(z.string().regex(/^[a-z][a-z0-9_]*$/), simulationValueSchema),
  /** Bozza di albero da simulare al posto di quello salvato (builder live). */
  condition_tree: commissioningKernelNodeSchema.optional(),
});

export type CommissioningVariableKernelFieldsInput =
  z.infer<typeof commissioningVariableKernelFieldsSchema>;
export type PublishCommissioningFunctionInput =
  z.infer<typeof publishCommissioningFunctionSchema>;
export type RotateCommissioningVariableVersionInput =
  z.infer<typeof rotateCommissioningVariableVersionSchema>;
export type SimulateCommissioningFunctionInput =
  z.infer<typeof simulateCommissioningFunctionSchema>;
